import InlineText from '../../InlineEdit/InlineText';
import InlineTextArea from '../../InlineEdit/InlineTextArea';
import { useIntl } from 'react-intl';
import messages from './messages';
import './Notice.scss';

const getLocalDateKey = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = `${now.getMonth() + 1}`.padStart(2, '0');
  const day = `${now.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const isDateKey = (value: string | undefined) =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);

const getScheduleState = (startDate?: string, endDate?: string) => {
  const hasStart = isDateKey(startDate);
  const hasEnd = isDateKey(endDate);

  if (hasStart && hasEnd && endDate! < startDate!) {
    return 'invalid';
  }

  const today = getLocalDateKey();

  if (hasStart && today < startDate!) return 'scheduled';
  if (hasEnd && today > endDate!) return 'expired';
  return 'live';
};

const formatScheduleDate = (intl: any, value?: string) => {
  if (!isDateKey(value)) return '';
  return intl.formatDate(new Date(`${value}T00:00:00`), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const ClosureIcon = ({ ariaLabel }: { ariaLabel: string }) => (
  <svg
    className="notice-block-icon"
    width="24"
    height="24"
    viewBox="0 0 256 256"
    role="img"
    aria-label={ariaLabel}
  >
    <rect
      x="40"
      y="40"
      width="176"
      height="176"
      rx="8"
      fill="none"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="176"
      y1="24"
      x2="176"
      y2="56"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="80"
      y1="24"
      x2="80"
      y2="56"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="40"
      y1="88"
      x2="216"
      y2="88"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="152"
      y1="128"
      x2="104"
      y2="176"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
    <line
      x1="152"
      y1="176"
      x2="104"
      y2="128"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="16"
    />
  </svg>
);

const View = (props: any) => {
  const intl = useIntl();
  const { data, isEditMode, block, onChangeBlock } = props;
  const variant = data.variant === 'banner' ? 'banner' : 'strip';
  const level = data.level === 'warning' ? 'warning' : 'info';
  const active = data.active !== false;
  const text = typeof data.text === 'string' ? data.text.trim() : '';
  const scheduleState = getScheduleState(data.start_date, data.end_date);
  const showTitle = variant === 'banner';
  const showClosureIcon = variant === 'banner' && level === 'warning';
  const showDateRange =
    variant === 'banner' &&
    isDateKey(data.start_date) &&
    isDateKey(data.end_date) &&
    (scheduleState === 'live' || scheduleState === 'scheduled');

  if (!isEditMode && (!active || !text || scheduleState !== 'live'))
    return null;

  const className = `notice-block notice-block--${level}${
    variant === 'banner' ? ' notice-block--banner' : ' notice-block--strip'
  }${!active && isEditMode ? ' notice-block--inactive' : ''}${
    scheduleState === 'scheduled' && isEditMode
      ? ' notice-block--scheduled'
      : ''
  }${
    scheduleState === 'expired' && isEditMode ? ' notice-block--expired' : ''
  }${
    scheduleState === 'invalid' && isEditMode ? ' notice-block--invalid' : ''
  }`;

  const statusMessages = [
    !active
      ? { key: 'inactive', text: intl.formatMessage(messages.inactiveStatus) }
      : null,
    scheduleState === 'scheduled'
      ? {
          key: 'scheduled',
          text: intl.formatMessage(messages.scheduledStatus, {
            date: formatScheduleDate(intl, data.start_date),
          }),
        }
      : null,
    scheduleState === 'expired'
      ? {
          key: 'expired',
          text: intl.formatMessage(messages.expiredStatus, {
            date: formatScheduleDate(intl, data.end_date),
          }),
        }
      : null,
    scheduleState === 'invalid'
      ? {
          key: 'invalid',
          text: intl.formatMessage(messages.invalidRangeStatus),
        }
      : null,
  ].filter(Boolean);

  return (
    <aside
      className={className}
      role={level === 'warning' ? 'alert' : 'note'}
      aria-label={intl.formatMessage(messages.announcementAria)}
    >
      <div className="notice-block-panel">
        <div className="notice-block-inner">
          {showClosureIcon && (
            <ClosureIcon
              ariaLabel={intl.formatMessage(messages.closureIconAria)}
            />
          )}
          {isEditMode ? (
            <>
              <InlineText
                value={data.eyebrow || ''}
                onChange={(eyebrow) =>
                  onChangeBlock(block, { ...data, eyebrow })
                }
                renderTag="span"
                renderClassName="notice-block-eyebrow"
                placeholder={intl.formatMessage(messages.eyebrowPlaceholder)}
                ariaLabel={intl.formatMessage(messages.eyebrowAria)}
              />
              {showTitle && (
                <InlineText
                  value={data.title || ''}
                  onChange={(title) => onChangeBlock(block, { ...data, title })}
                  renderTag="h2"
                  renderClassName="notice-block-title"
                  placeholder={intl.formatMessage(messages.titlePlaceholder)}
                  ariaLabel={intl.formatMessage(messages.titleAria)}
                />
              )}
              <InlineTextArea
                value={data.text || ''}
                onChange={(nextText) =>
                  onChangeBlock(block, { ...data, text: nextText })
                }
                renderTag="p"
                renderClassName="notice-block-text"
                placeholder={intl.formatMessage(messages.textPlaceholder)}
                ariaLabel={intl.formatMessage(messages.textAria)}
              />
            </>
          ) : (
            <>
              {data.eyebrow && (
                <span className="notice-block-eyebrow">{data.eyebrow}</span>
              )}
              {showTitle && data.title && (
                <h2 className="notice-block-title">{data.title}</h2>
              )}
              <p className="notice-block-text">{data.text}</p>
            </>
          )}

          {showDateRange && (
            <p className="notice-block-daterange">
              {intl.formatMessage(messages.dateRangePublic, {
                start: formatScheduleDate(intl, data.start_date),
                end: formatScheduleDate(intl, data.end_date),
              })}
            </p>
          )}

          {isEditMode && statusMessages.length > 0 && (
            <ul
              className="notice-block-statuses"
              aria-label={intl.formatMessage(messages.visibilityStatus)}
            >
              {statusMessages.map((status: any) => (
                <li
                  key={status.key}
                  className={`notice-block-status notice-block-status--${status.key}`}
                >
                  {status.text}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </aside>
  );
};

export default View;
